import { useRef } from "react";
import { Button } from "~/components/ui";
import type { AppLanguageType } from "@noa/i18n";
import { formatLocaleDate, type TimelineTableItem } from "@noa/ui";
import { ChatMessageAnchor } from "./chat-message-anchor";
import { AuthenticatedImage } from "./chat-message-authenticated-image";
import { Maximize2 } from "lucide-react";
import cn from "classnames";
import { useOverflowCount } from "~/hooks/use-overflow-count";

interface TimelineCardProps {
  item: TimelineTableItem;
  distance: number;
  locale: AppLanguageType;
  onExpand: () => void;
}

function ImageRow({ imageUrls, alt }: { imageUrls: string[]; alt: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const overflowCount = useOverflowCount(containerRef);

  return (
    <div className="relative pt-1">
      <div ref={containerRef} className="flex flex-nowrap overflow-x-hidden gap-2 px-0.5 pt-0.5">
        {imageUrls.map((url, i) => (
          <AuthenticatedImage
            key={i}
            url={url}
            alt={alt}
            hoverPreview
            classNames={{
              root: "inline-block shrink-0",
              image: "h-11 w-auto min-w-11 rounded cursor-pointer",
            }}
          />
        ))}
      </div>
      {overflowCount > 0 && (
        <div className="absolute -right-2 top-0 -translate-y-1/2 flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-[10px] font-medium leading-none bg-noa-hover text-noa-muted">
          +{overflowCount}
        </div>
      )}
    </div>
  );
}

export function CardTitle({ item, locale }: { item: TimelineTableItem; locale: AppLanguageType }) {
  if (item.titleType === "id") {
    return item.titleLink ? (
      <ChatMessageAnchor href={item.titleLink}>{item.title}</ChatMessageAnchor>
    ) : (
      <>{item.title}</>
    );
  }
  return <>{formatLocaleDate(item.title, locale)}</>;
}

export function TimelineCard({ item, distance, locale, onExpand }: TimelineCardProps) {
  const isSelected = distance === 0;

  return (
    <div
      className={cn(
        "chat-timeline-card h-full pt-2.5 pb-3.5 px-3.5 flex flex-col gap-2.5 overflow-y-clip box-border border",
        isSelected
          ? "opacity-100 border-noa bg-noa shadow-[4px_4px_0_var(--border)]"
          : "opacity-[0.85] border-noa bg-transparent shadow-none"
      )}
      style={{ overflowClipMargin: "content-box" }}
    >
      <div className="flex items-center justify-between shrink-0 pt-1 gap-1">
        <div className="chat-timeline-card-date text-[10px] opacity-60 font-mono tracking-[0.3px] truncate">
          <CardTitle item={item} locale={locale} />
        </div>
        <Button
          variant="default"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
        >
          <Maximize2 size={16} />
        </Button>
      </div>

      {item.cardDetailedText && item.cardDetailedText.length > 0 && (
        <div className="flex flex-col gap-1">
          {item.cardDetailedText.map((detail, i) => (
            <div key={i} className="flex flex-col gap-1">
              {detail.text && (
                <div
                  className={cn(
                    "chat-timeline-card-details leading-normal line-clamp-2",
                    isSelected ? "text-xs" : "text-[10px]"
                  )}
                >
                  {detail.text}
                </div>
              )}
              {detail.imageUrls.length > 0 && <ImageRow imageUrls={detail.imageUrls} alt={detail.text} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
